import { useClickOutside } from "@/hooks/useClickOutside";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import type { ComboboxItem } from "@/lib/form";
import { cn } from "@/lib/utils";
import { Check, Search } from "lucide-react";
import { useEffect, useId, useMemo, useRef, useState } from "react";
import { Chip } from "./ui/chip";
import { Spinner } from "./ui/spinner";

interface ComboboxProps {
  id: string;
  placeholder: string;
  selected: ComboboxItem[];
  onChange: (items: ComboboxItem[]) => void;
  /** Busca remota; recebe o termo e um AbortSignal para cancelamento. */
  search: (query: string, signal: AbortSignal) => Promise<ComboboxItem[]>;
  minChars?: number;
  noResultsText?: string;
  disabled?: boolean;
}

/**
 * Combobox de busca remota com múltipla seleção em chips. A digitação é
 * debounced (300ms) e cada busca cancela a anterior via AbortSignal. Usada para
 * CNAE e natureza jurídica.
 */
export function Combobox({
  id,
  placeholder,
  selected,
  onChange,
  search,
  minChars = 1,
  noResultsText = "Nenhum resultado",
  disabled = false,
}: ComboboxProps) {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [results, setResults] = useState<ComboboxItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);

  const debounced = useDebouncedValue(input, 300);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listboxId = useId();

  // Mantém a função de busca em ref para não re-disparar o efeito quando o pai
  // recria a referência a cada render.
  const searchRef = useRef(search);
  useEffect(() => {
    searchRef.current = search;
  });

  useClickOutside(containerRef, () => setOpen(false), open);

  const selectedCodes = useMemo(() => new Set(selected.map((item) => item.codigo)), [selected]);
  const query = debounced.trim();
  const belowMinChars = query.length < minChars;

  useEffect(() => {
    if (query.length < minChars) {
      setResults([]);
      setLoading(false);
      setFailed(false);
      return;
    }
    const controller = new AbortController();
    setLoading(true);
    setFailed(false);
    searchRef
      .current(query, controller.signal)
      .then((items) => {
        setResults(items);
        setLoading(false);
      })
      .catch(() => {
        if (controller.signal.aborted) {
          return;
        }
        setFailed(true);
        setLoading(false);
      });
    return () => controller.abort();
  }, [query, minChars]);

  const toggleItem = (item: ComboboxItem) => {
    if (selectedCodes.has(item.codigo)) {
      onChange(selected.filter((current) => current.codigo !== item.codigo));
    } else {
      onChange([...selected, item]);
    }
    setInput("");
    inputRef.current?.focus();
  };

  const removeItem = (codigo: number) => {
    onChange(selected.filter((current) => current.codigo !== codigo));
  };

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <Search
          className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
          aria-hidden="true"
        />
        <input
          ref={inputRef}
          id={id}
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-controls={listboxId}
          aria-autocomplete="list"
          autoComplete="off"
          value={input}
          placeholder={placeholder}
          disabled={disabled}
          onChange={(event) => {
            setInput(event.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              // Evita submeter o formulário; seleciona o primeiro resultado.
              event.preventDefault();
              const first = results[0];
              if (!belowMinChars && !loading && first) {
                toggleItem(first);
              }
            } else if (event.key === "Escape") {
              setOpen(false);
            }
          }}
          className="h-10 w-full rounded-md border border-input bg-card pl-9 pr-9 text-sm text-foreground shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:bg-muted disabled:opacity-60"
        />
        {loading ? (
          <Spinner className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        ) : null}
      </div>

      {selected.length > 0 ? (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {selected.map((item) => (
            <Chip key={item.codigo} label={item.label} onRemove={() => removeItem(item.codigo)} />
          ))}
        </div>
      ) : null}

      {open && !disabled ? (
        <div
          id={listboxId}
          role="listbox"
          aria-label={placeholder}
          className="absolute z-20 mt-1 max-h-64 w-full overflow-y-auto rounded-md border border-border bg-popover p-1 shadow-lg"
        >
          {belowMinChars ? (
            <p className="px-3 py-4 text-center text-sm text-muted-foreground">
              Digite para buscar…
            </p>
          ) : loading ? (
            <p className="px-3 py-4 text-center text-sm text-muted-foreground">Buscando…</p>
          ) : failed ? (
            <p className="px-3 py-4 text-center text-sm text-destructive">
              Falha ao buscar. Tente novamente.
            </p>
          ) : results.length === 0 ? (
            <p className="px-3 py-4 text-center text-sm text-muted-foreground">{noResultsText}</p>
          ) : (
            results.map((item) => {
              const isSelected = selectedCodes.has(item.codigo);
              return (
                <button
                  key={item.codigo}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  onClick={() => toggleItem(item)}
                  className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm text-foreground transition-colors hover:bg-muted"
                >
                  <span
                    className={cn(
                      "flex h-4 w-4 shrink-0 items-center justify-center rounded border",
                      isSelected
                        ? "border-primary bg-primary text-primary-foreground"
                        : "border-slate-300",
                    )}
                  >
                    {isSelected ? <Check className="h-3 w-3" aria-hidden="true" /> : null}
                  </span>
                  <span className="truncate">{item.label}</span>
                </button>
              );
            })
          )}
        </div>
      ) : null}
    </div>
  );
}
